import { forwardRef, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useApi } from '@/lib/legacy';
import { notifySuccess } from '@/lib/notify';
import { currencyFormatter as currencyFormatterJs } from '../../QuotePage/content/QuoteGrids/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from '../context/PolicyPageContext';
import { ActionButton, ActionRow, Grid, IncludeInFormsCheckbox, type Row } from '../tabParts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => { endorsements?: Row[] };

const columns = [
  { headerName: 'Lender Loss Payee', field: 'lienHolder', editable: true },
  { headerName: 'Address', field: 'lienHolderAddress', editable: true },
  { headerName: 'City, State Zip', field: 'lienHolderCityStateZip', editable: true },
  { headerName: 'Locations', field: 'lienHolderLocations', editable: true },
  { headerName: 'Premium', field: 'premium', editable: true, valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }) },
];

/** The lender loss payee: its one row, saving it into the forms, or removing its form. */
export const LenderLossPayee = forwardRef<unknown, { policy: Row; setLenderLossPayeeRef: (ref: unknown) => void; lenderLossPayeeRef?: unknown }>(
  ({ policy, setLenderLossPayeeRef }) => {
    const { createEndorsement, downloadEndorsement, removeEndorsementForm } = useApi();
    const { endorsements = [] } = usePolicyPageContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const policyEffectiveDate = moment.utc(policy.effectiveAt).format('YYYY-MM-DD');

    const [endorsementData, setEndorsementData] = useState<Row>({
      lienHolder: '',
      lienHolderAddress: '',
      lienHolderCityStateZip: '',
      lienHolderLocations: '',
      premium: 0,
    });
    const [ordinal, setOrdinal] = useState<unknown>();
    const [include, setInclude] = useState(false);
    const [hasExistingEndorsement, setHasExistingEndorsement] = useState(false);

    useEffect(() => {
      if (endorsements.length) {
        const lenderLossPayee = endorsements.find(({ endorsementType }) => endorsementType === 'lenderLossPayee');
        if (lenderLossPayee) {
          setEndorsementData(lenderLossPayee.endorsementData);
          setOrdinal(lenderLossPayee.ordinal);
          setInclude(true);
          setHasExistingEndorsement(true);
        } else {
          setInclude(false);
          setHasExistingEndorsement(false);
        }
      }
    }, [endorsements]);

    const payload = { endorsementType: 'lenderLossPayee', endorsementData: { policyEffectiveDate, ...endorsementData }, ordinal };

    const handleOnSave = async () => {
      setIsLoading(true);
      try {
        if (include) {
          const response = await createEndorsement(policy.id, payload);
          if (response) {
            window.open(response.value, '_blank');
          }
        } else {
          await removeEndorsementForm(policy.id, { endorsementType: 'lenderLossPayee' });
          notifySuccess('Form removed from policy successfully.');
        }
      } catch (error) {
        console.error('Error saving payload:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleOnDownload = async () => {
      setIsDownloading(true);
      try {
        const response = await downloadEndorsement(policy.id, payload);
        if (response) {
          window.open(response.data.value, '_blank');
        }
      } catch (error) {
        console.error('Error saving payload:', error);
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <section role="group" aria-label="Lender Loss Payee" className="flex flex-col">
        <h4 className="m-2.5 mt-6 text-lg font-medium">Lender Loss Payee</h4>
        <div className="grid-container max-h-[600px] overflow-y-auto">
          <Grid
            data={[endorsementData]}
            columnDefs={columns}
            defaultColDef={{ flex: 1, resizable: true }}
            setGridReference={setLenderLossPayeeRef}
            floatingFilter={false}
            shouldShowPagination={false}
            onCellValueChanged={(params: { colDef: { field: string }; data: Row }) => {
              if (!hasExistingEndorsement) {
                setInclude(true);
              }
              setEndorsementData({ ...endorsementData, [params.colDef.field]: params.data[params.colDef.field] });
            }}
          />
        </div>
        <ActionRow>
          <ActionButton onClick={handleOnSave} loading={isLoading}>
            Save
          </ActionButton>
          <ActionButton onClick={handleOnDownload} loading={isDownloading}>
            Download
          </ActionButton>
          <div className="m-4">
            <IncludeInFormsCheckbox checked={include} onChange={setInclude} />
          </div>
        </ActionRow>
      </section>
    );
  }
);
LenderLossPayee.displayName = 'LenderLossPayee';
